import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import React, { useCallback, useMemo, useState } from 'react';
import { Theme } from '@descope/page-editor-components';
import TypographyVariantSettings from './TypographyVariantSettings';
import TypographyFonts from './TypographyFonts';
import TypographyList from './TypographyList';
import {
	FontFamily,
	TypographyVariants,
	UpdateTypography,
	VariantsList
} from './types';
import { variantNamesOrder } from './constants';

type Props = {
	values: Theme['typography'];
	updateTypography: UpdateTypography;
};

const Typography = ({ values, updateTypography }: Props) => {
	const [selectedVariant, setSelectedVariant] =
		useState<TypographyVariants>('h1');

	const setFontFamily = useCallback(
		(index: number, value: FontFamily) => {
			updateTypography(value, `fontFamilies[${index}]`);
		},
		[updateTypography]
	);

	const setVariant = useCallback(
		(path: string, value: string) => {
			updateTypography(value, `variants.${selectedVariant}.${path}`);
		},
		[selectedVariant, updateTypography]
	);

	const fontFamiliesNames = useMemo(
		() => values?.fontFamilies?.map((family) => family.label),
		[values?.fontFamilies]
	);

	return (
		<Box sx={{ flexGrow: 1 }}>
			<Grid container>
				<Grid item xs={12} borderBottom={1} borderColor="divider">
					<TypographyFonts
						setFontFamily={setFontFamily}
						fontFamilies={values?.fontFamilies}
					/>
				</Grid>
				<Grid item xs={4} borderRight={1} borderColor="divider">
					<TypographyList
						selected={selectedVariant}
						setSelected={setSelectedVariant}
						variants={
							Object.keys(values?.variants || {}).sort(
								// missing elements are last
								(a: string, b: string) =>
									// eslint-disable-next-line no-bitwise
									(~variantNamesOrder.indexOf(b) || -variantNamesOrder.length) -
									// eslint-disable-next-line no-bitwise
									(~variantNamesOrder.indexOf(a) || -variantNamesOrder.length)
							) as VariantsList
						}
					/>
				</Grid>
				<Grid item xs={8}>
					<TypographyVariantSettings
						fontFamilies={fontFamiliesNames}
						values={values?.variants?.[selectedVariant as TypographyVariants]}
						setVariant={setVariant}
						variant={selectedVariant}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};

export default React.memo(Typography);
